import clsx from 'clsx';
import { ChangeEvent, FC, HTMLProps } from 'react';
import { useThemeColorPaletteAsCssTokens } from '../../hooks/use-theme-color-palette-as-css-tokens';
import { ThemeColors } from '../../theme';
import classes from './checkbox-input.module.scss';

export type CheckboxInputProps = Omit<HTMLProps<HTMLInputElement>, 'onChange'> & {
  /**
   * Callback function triggered when a change event occurs on an input element.
   * Typically used to handle user input changes and update the corresponding state or value.
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;

  /**
   * Represents the theme color used for styling purposes.
   * The value is optional and can be selected from the predefined `ThemeColors`.
   * It allows customization of component appearance according to the theme.
   */
  color?: ThemeColors;
};

/**
 * CheckboxInput is a functional component that renders a customizable checkbox input element.
 * It accepts all standard properties applicable to an HTML input element of type "checkbox",
 * along with additional styling and behavior customizations through provided class names
 * and conditional class handling for a disabled state.
 *
 * The component combines base styles with user-defined class names and applies a specific
 * class when the checkbox is disabled.
 *
 * Note: The component utilizes the `clsx` utility library for managing className composition.
 *
 * Props:
 * - Accepts all standard properties for an HTML input element of type "checkbox".
 * - Includes optional className for additional styling and `disabled` for state-based styling.
 */
export const CheckboxInput: FC<CheckboxInputProps> = ({ color = 'black', ...props }) => {
  const cssTokens = useThemeColorPaletteAsCssTokens('--checkbox', color);

  return (
    <input
      type="checkbox"
      {...props}
      checked={props.checked}
      style={cssTokens}
      className={clsx(classes.root, props.className, {
        [classes.isDisabled]: props.disabled,
        [classes.isChecked]: props.checked,
      }, 'checkbox-input-component')}
    />
  );
};
